import { createClient, Session } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || 'https://prurnebjylixteaxaepa.supabase.co';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBydXJuZWJqeWxpeHRlYXhhZXBhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MjYyNDEzODQsImV4cCI6MjA0MTgxNzM4NH0.qSF5e1J-ZLv9n7WivhCRIQv4cDtIGur5SOAAUH49GNI';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        // Mantém a sessão sincronizada entre abas
        storageKey: 'coffee-selection-auth',
    },
});

/**
 * Retorna a sessão válida atual ou null.
 * Usa getSession() que automaticamente renova tokens expirados
 * graças ao autoRefreshToken: true.
 */
export async function getValidSession(): Promise<Session | null> {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) return null;
    return session;
}

export type SupabaseFailureKind = 'auth' | 'network' | 'timeout' | 'rls' | 'unknown';

export class SupabaseOperationError extends Error {
    kind: SupabaseFailureKind;
    originalError: unknown;

    constructor(message: string, kind: SupabaseFailureKind, originalError: unknown) {
        super(message);
        this.name = 'SupabaseOperationError';
        this.kind = kind;
        this.originalError = originalError;
    }
}

const SESSION_REFRESH_WINDOW_MS = 60_000;

const normalizeUnknownError = (error: unknown): Error => {
    if (error instanceof Error) return error;
    if (typeof error === 'string') return new Error(error);
    return new Error('Erro desconhecido ao processar operação no Supabase.');
};

const classifySupabaseError = (error: unknown): SupabaseFailureKind => {
    const normalized = normalizeUnknownError(error);
    const message = normalized.message.toLowerCase();
    const code = (error as any)?.code;
    const status = (error as any)?.status;

    if (code === '42501') return 'rls';

    if (
        status === 401 ||
        status === 403 ||
        message.includes('jwt') ||
        message.includes('token') ||
        message.includes('auth') ||
        message.includes('session')
    ) {
        return 'auth';
    }

    if (message.includes('timeout') || message.includes('timed out') || normalized.name === 'AbortError') {
        return 'timeout';
    }

    if (
        message.includes('network') ||
        message.includes('fetch') ||
        message.includes('failed to fetch') ||
        message.includes('offline')
    ) {
        return 'network';
    }

    return 'unknown';
};

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, operationName: string): Promise<T> => {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutHandle = setTimeout(() => {
            reject(new SupabaseOperationError(
                `Tempo excedido ao executar ${operationName}.`,
                'timeout',
                new Error('TIMEOUT')
            ));
        }, timeoutMs);
    });

    try {
        return await Promise.race([promise, timeoutPromise]);
    } finally {
        if (timeoutHandle) clearTimeout(timeoutHandle);
    }
};

export async function ensureSessionReady(): Promise<Session | null> {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) return null;

    const expiresAtMs = (session.expires_at ?? 0) * 1000;
    const shouldRefresh = expiresAtMs > 0 && expiresAtMs - Date.now() <= SESSION_REFRESH_WINDOW_MS;

    if (!shouldRefresh) return session;

    const { data, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
        throw new SupabaseOperationError(
            'Nao foi possivel renovar a sessao automaticamente.',
            'auth',
            refreshError
        );
    }

    return data.session;
}

type ResilientOptions = {
    operationName?: string;
    timeoutMs?: number;
    retries?: number;
    recoverSessionOnAuthError?: boolean;
};

export async function runSupabaseOperation<T>(
    operation: () => Promise<T>,
    options: ResilientOptions = {}
): Promise<T> {
    const {
        operationName = 'operacao no Supabase',
        timeoutMs = 12_000,
        retries = 1,
        recoverSessionOnAuthError = true,
    } = options;

    let attempt = 0;
    let lastError: SupabaseOperationError | null = null;

    while (attempt <= retries) {
        try {
            if (attempt === 0) {
                await ensureSessionReady();
            }

            return await withTimeout(operation(), timeoutMs, operationName);
        } catch (error) {
            if (error instanceof SupabaseOperationError) {
                lastError = error;
            } else {
                const normalized = normalizeUnknownError(error);
                const kind = classifySupabaseError(error);
                lastError = new SupabaseOperationError(normalized.message, kind, error);
            }

            const canRetry = attempt < retries;

            if (
                lastError.kind === 'auth' &&
                recoverSessionOnAuthError &&
                canRetry
            ) {
                const { error: refreshError } = await supabase.auth.refreshSession();
                if (!refreshError) {
                    attempt += 1;
                    continue;
                }
            }

            if ((lastError.kind === 'network' || lastError.kind === 'timeout') && canRetry) {
                attempt += 1;
                continue;
            }

            break;
        }
    }

    throw lastError || new SupabaseOperationError(
        `Falha ao executar ${operationName}.`,
        'unknown',
        null
    );
}
